import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { cardArtUrl, cardTitle, onCardArtError } from '../MemberPortal/NFTCollection/components/CardInspectModal';
import './PullPage.css';

/* A shared pack pull — the public page behind the reveal's SHARE YOUR PULL button
 * (server GET /pulls/:token, lib/packPulls). Anyone with the link sees what the
 * member pulled, with the estimated values at the time, can tap a card to see it
 * large, and is invited to sign up through the member's referral link. Signed out
 * by design: fetched with credentials omitted, like the public card catalogue, so a
 * visitor's missing cookies can never make it 401. */

type PullCard = {
  id: number;
  name: string;
  type: string;
  rarity: string;
  valueInc: number | null;
  valueUsd: number | null;
};
type Pull = {
  token: string;
  packageName: string;
  pulledBy: string;
  referralCode: string | null;
  cards: PullCard[];
  totals: { inc: number; usd: number | null; priced: number };
  createdAt: string;
};
type Group = { card: PullCard; n: number };

const RANK: Record<string, number> = { common: 0, rare: 1, epic: 2, legendary: 3, relic: 4 };
const fmtInc = (v: number) =>
  (v >= 100 ? Math.round(v) : Math.round(v * 100) / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 });
const fmtUsd = (v: number) => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rarityKey = (r: string) => String(r || '').toLowerCase();

/* The reveal's APPRAISAL readout, as a component: the INC on odometer drums that roll
   to rest left to right, the plate locking, the dollars popping after the last drum —
   the same timing as packRevealEngine.appraise (900 ms roll + 85 ms a drum). A cut
   under reduced motion. */
const ROLL_MS = 900;
const DRUM_MS = 85;
const DIGITS = '01234567890123456789'.split('');
const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Appraisal = ({ label, inc, usd, big }: { label: string; inc: number | null; usd: number | null; big?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'wait' | 'on' | 'locked'>('wait');
  const text = inc != null && inc > 0 ? fmtInc(inc) : null;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const instant = reducedMotion();
    const strips = Array.from(el.querySelectorAll<HTMLElement>('.pa-od-strip'));
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        setPhase('on');
        strips.forEach((strip, i) => {
          const d = Number(strip.dataset.d);
          const go = () => { strip.style.transform = `translateY(-${10 + d}em)`; }; // one em per digit, as the reveal's drums
          if (instant) { strip.style.transition = 'none'; go(); }
          else timers.push(window.setTimeout(go, i * DRUM_MS));
        });
        timers.push(window.setTimeout(() => setPhase('locked'), instant ? 0 : ROLL_MS + strips.length * DRUM_MS));
      }, instant ? 0 : 420)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [text]);
  return (
    <div ref={ref} className={`pa ${phase !== 'wait' ? 'on' : ''} ${phase === 'locked' ? 'locked' : ''} ${text ? '' : 'none'} ${big ? 'big' : ''}`}>
      <div className="pa-head">
        <span>{label}</span>
        <span className={`pa-state ${phase === 'wait' ? 'wait' : ''}`}>
          {phase === 'wait' ? 'Scanning' : text ? 'Market value' : 'No market price yet'}
        </span>
      </div>
      <div className="pa-row">
        <span className="pa-inc">
          {text
            ? text.split('').map((ch, i) =>
                /\d/.test(ch) ? (
                  <span className="pa-od" key={i}>
                    <span className="pa-od-strip" data-d={ch}>
                      {DIGITS.map((x, j) => (
                        <span className="pa-od-d" key={j}>{x}</span>
                      ))}
                    </span>
                  </span>
                ) : (
                  <span className="pa-od-sep" key={i}>{ch}</span>
                )
              )
            : phase === 'wait' ? '' : '—'}
        </span>
        {text && <span className="pa-unit">INC</span>}
        {text && usd != null && <span className={`pa-usd ${phase === 'locked' ? 'pop' : ''}`}>{fmtUsd(usd)}</span>}
      </div>
    </div>
  );
};

const PullPage = () => {
  const { token } = useParams<{ token: string }>();
  const [pull, setPull] = useState<Pull | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'missing' | 'error'>('loading');
  const [open, setOpen] = useState<Group | null>(null);

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL;
    if (!token || !/^[A-Za-z0-9_-]{8,32}$/.test(token)) {
      setState('missing');
      return;
    }
    const ctrl = new AbortController();
    fetch(`${base}/pulls/${encodeURIComponent(token)}`, { credentials: 'omit', signal: ctrl.signal })
      .then(async (r) => {
        if (r.status === 404) return setState('missing');
        if (!r.ok) throw new Error(String(r.status));
        const data = (await r.json()) as Pull;
        setPull(data);
        setState('ok');
      })
      .catch((e) => {
        if (e && e.name === 'AbortError') return;
        setState('error');
      });
    return () => ctrl.abort();
  }, [token]);

  // one tile per distinct card (×N), best first — the same order as the haul
  const groups = useMemo<Group[]>(() => {
    const by = new Map<string, Group>();
    for (const c of pull?.cards || []) {
      const k = `${c.type}/${c.name}/${c.rarity}`;
      const g = by.get(k);
      if (g) g.n++;
      else by.set(k, { card: c, n: 1 });
    }
    return [...by.values()].sort(
      (a, b) =>
        (RANK[rarityKey(b.card.rarity)] || 0) - (RANK[rarityKey(a.card.rarity)] || 0) ||
        (b.card.valueInc || 0) - (a.card.valueInc || 0)
    );
  }, [pull]);

  const signUpTo = pull?.referralCode ? `/sign-up?referralCode=${encodeURIComponent(pull.referralCode)}` : '/sign-up';
  const n = pull?.cards.length || 0;
  const epicPlus = (pull?.cards || []).filter((c) => (RANK[rarityKey(c.rarity)] || 0) >= 2).length;
  const when = pull ? new Date(pull.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <Box className="pull-page" sx={{ background: '#070c13', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteHeader />
      <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: 1100, mx: 'auto', px: 2, py: { xs: 3, md: 5 } }}>
        {state === 'loading' && <div className="pull-status">Loading the pull…</div>}
        {state === 'missing' && (
          <div className="pull-status">
            <div className="pull-status-title">This pull link is not valid</div>
            <p>It may have been mistyped. You can still open packs of your own.</p>
            <Button component={Link} to="/sign-up" className="pull-cta-btn" variant="contained">
              Join INCRYPT
            </Button>
          </div>
        )}
        {state === 'error' && <div className="pull-status">Could not load this pull right now. Please try again shortly.</div>}
        {state === 'ok' && pull && (
          <>
            <header className="pull-hero">
              <div className="pull-eyebrow">PACK PULL · {when}</div>
              <h1 className="pull-title">
                <span className="pull-who">{pull.pulledBy}</span> pulled {n} card{n === 1 ? '' : 's'}
              </h1>
              <div className="pull-sub">
                {pull.packageName ? `${pull.packageName} · ` : ''}
                {groups.length} unique{epicPlus ? ` · ${epicPlus} Epic+` : ''}
              </div>
              {pull.totals.priced > 0 && (
                <div className="pull-plate">
                  <Appraisal label="This pull is worth" inc={pull.totals.inc} usd={pull.totals.usd} big />
                  <div className="pull-plate-note">
                    Estimated from average marketplace listings when it was pulled
                    {pull.totals.priced < n ? ` · ${n - pull.totals.priced} card${n - pull.totals.priced === 1 ? '' : 's'} had no listing yet` : ''}
                  </div>
                </div>
              )}
            </header>

            <section className="pull-grid" aria-label="The cards pulled">
              {groups.map((g) => (
                <button
                  key={`${g.card.type}/${g.card.name}/${g.card.rarity}`}
                  type="button"
                  className={`pull-tile r-${rarityKey(g.card.rarity)}`}
                  onClick={() => setOpen(g)}
                  aria-label={`${cardTitle(g.card.name)}, ${g.card.rarity}${g.n > 1 ? `, times ${g.n}` : ''}`}
                >
                  <img src={cardArtUrl(g.card)} alt="" loading="lazy" decoding="async" onError={onCardArtError} />
                  {g.n > 1 && <span className="pull-qty">x{g.n}</span>}
                  <span className="pull-tile-info">
                    <span className="pull-tile-name">{cardTitle(g.card.name)}</span>
                    <span className="pull-tile-rar">{g.card.rarity}</span>
                    {g.card.valueInc != null && g.card.valueInc > 0 && (
                      <span className="pull-tile-val">{fmtInc(g.card.valueInc)} INC</span>
                    )}
                  </span>
                </button>
              ))}
            </section>
            <div className="pull-hint">Tap a card to see it up close</div>

            <section className="pull-cta">
              <div className="pull-cta-title">Want a pull like this?</div>
              <p className="pull-cta-text">
                Open your own INCRYPT packs, build a deck and play in the Duel Arena.
                {pull.referralCode ? ` Sign up with ${pull.pulledBy}'s link and you're in together.` : ''}
              </p>
              <div className="pull-cta-row">
                <Button component={Link} to={signUpTo} className="pull-cta-btn" variant="contained">
                  {pull.referralCode ? `Join with ${pull.pulledBy}'s link` : 'Join INCRYPT'}
                </Button>
                <Button component={Link} to="/trading-card-game" className="pull-cta-ghost" variant="outlined">
                  About the game
                </Button>
              </div>
            </section>
          </>
        )}
      </Box>
      <RaffleFooter />

      <Dialog open={!!open} onClose={() => setOpen(null)} PaperProps={{ className: 'pull-modal-paper' }} className="pull-modal">
        {open && (
          <div className={`pull-modal-card r-${rarityKey(open.card.rarity)}`}>
            <div className="pull-modal-art" onClick={() => setOpen(null)} role="presentation">
              <img src={cardArtUrl(open.card)} alt={cardTitle(open.card.name)} onError={onCardArtError} />
            </div>
            <div className="pull-modal-info">
              <div className="pull-modal-name">{cardTitle(open.card.name)}</div>
              <div className="pull-modal-rar">
                {open.card.rarity}
                {open.n > 1 ? ` · ×${open.n}` : ''}
              </div>
            </div>
            <div className="pull-modal-val">
              <Appraisal key={`${open.card.id}-${open.card.rarity}`} label="Appraisal" inc={open.card.valueInc} usd={open.card.valueUsd} />
              {open.n > 1 && open.card.valueInc != null && open.card.valueInc > 0 && (
                <div className="pull-modal-val-all">
                  ×{open.n} = {fmtInc(open.card.valueInc * open.n)} INC
                  {open.card.valueUsd != null ? ` · ${fmtUsd(open.card.valueUsd * open.n)}` : ''}
                </div>
              )}
            </div>
            <button type="button" className="pull-modal-close" onClick={() => setOpen(null)}>
              Close
            </button>
          </div>
        )}
      </Dialog>
    </Box>
  );
};

export default PullPage;
